import { useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Link2, FileText } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { Field } from "@/routes/FinanceAccounts";

/**
 * /recipes/new — three input modes:
 *   1. Paste URL (worker scrapes via schema.org/Recipe → Anthropic fallback)
 *   2. Manual title + ingredients + steps
 *   3. (Photo extract — TODO)
 */

export function RecipeNew() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<"url" | "manual">("url");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [servings, setServings] = useState("4");
  const [ingredients, setIngredients] = useState("");
  const [instructions, setInstructions] = useState("");

  const importUrl = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/recipes/import-url", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({ url }),
      });
      if (!res.ok) throw new Error(await res.text());
      const json = (await res.json()) as { recipe_id?: string };
      return json.recipe_id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ["recipes", userId] });
      if (id) navigate(`/recipes/${id}`);
    },
  });

  const saveManual = useMutation({
    mutationFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("recipe")
        .insert({
          user_id: userId,
          title: title.trim() || "Untitled recipe",
          source_type: "manual",
          servings: Number(servings) || 1,
          instructions: instructions.trim() || null,
        })
        .select("id")
        .single();
      if (error) throw error;
      const recipeId = (data as { id: string }).id;
      const lines = ingredients
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      if (lines.length > 0) {
        const ingRows = lines.map((raw_text, i) => ({
          recipe_id: recipeId,
          line_number: i + 1,
          raw_text,
        }));
        await (supabase as never as { from: (t: string) => any })
          .from("recipe_ingredient")
          .insert(ingRows);
      }
      return recipeId;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ["recipes", userId] });
      if (id) navigate(`/recipes/${id}`);
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 600, margin: "0 auto" }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
        <Link to="/recipes" style={{ color: "var(--fg-3)", textDecoration: "none" }}>
          <ArrowLeft size={16} style={{ verticalAlign: "middle" }} /> Recipes
        </Link>
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 28 }}>
          New recipe
        </h1>
      </header>

      <div style={{ display: "flex", gap: 8 }}>
        <ModeBtn active={mode === "url"} onClick={() => setMode("url")} icon={<Link2 size={14} />} label="From URL" />
        <ModeBtn active={mode === "manual"} onClick={() => setMode("manual")} icon={<FileText size={14} />} label="Manual" />
      </div>

      {mode === "url" ? (
        <>
          <Field label="Recipe URL">
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://cooking.nytimes.com/recipes/…"
              style={inputStyle}
            />
          </Field>
          <p style={{ margin: 0, color: "var(--fg-3)", fontSize: 12 }}>
            Haus will fetch the page, extract the recipe (schema.org/Recipe → Anthropic fallback),
            and link ingredients to the food DB where possible.
          </p>
          <button
            onClick={() => importUrl.mutate()}
            disabled={!url || importUrl.isPending}
            style={primaryBtn}
          >
            {importUrl.isPending ? "Importing…" : "Import"}
          </button>
          {importUrl.isError && (
            <span style={{ color: "var(--severity-bad)", fontSize: 12 }}>
              {(importUrl.error as Error).message}
            </span>
          )}
        </>
      ) : (
        <>
          <Field label="Title">
            <input value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} placeholder="e.g. Lentil dal" />
          </Field>
          <Field label="Servings">
            <input
              type="number"
              inputMode="numeric"
              value={servings}
              onChange={(e) => setServings(e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Ingredients (one per line)">
            <textarea
              value={ingredients}
              onChange={(e) => setIngredients(e.target.value)}
              style={{ ...inputStyle, minHeight: 120, fontFamily: "var(--font-mono)" }}
              placeholder={"200g red lentils\n1 onion, diced\n2 cloves garlic\n…"}
            />
          </Field>
          <Field label="Instructions (markdown)">
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              style={{ ...inputStyle, minHeight: 160 }}
              placeholder="1. Sweat onion + garlic in olive oil...&#10;2. Add lentils + 600ml water..."
            />
          </Field>
          <button onClick={() => saveManual.mutate()} disabled={!title || saveManual.isPending} style={primaryBtn}>
            {saveManual.isPending ? "Saving…" : "Save recipe"}
          </button>
        </>
      )}
    </div>
  );
}

function ModeBtn({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: 10,
        borderRadius: 10,
        border: "1px solid var(--hair-2)",
        background: active ? "var(--accent)" : "transparent",
        color: active ? "var(--accent-on)" : "var(--fg)",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        fontSize: "var(--type-body-s)",
      }}
    >
      {icon} {label}
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid var(--hair-2)",
  background: "var(--bg)",
  color: "var(--fg)",
  fontSize: "var(--type-body-s)",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box",
};

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--accent-on)",
  padding: "12px",
  border: "none",
  borderRadius: 10,
  fontSize: "var(--type-body)",
  fontWeight: 500,
  cursor: "pointer",
};
